/**
 * GoalCell — editable goal text for a measure.
 *
 * Save semantics:
 *   - Local state for the typed value.
 *   - On blur, if the value differs from the server value, fire the PUT.
 *   - While the PUT is in flight, the input is disabled.
 *   - On success the dashboard query invalidates; `current_goal` arrives
 *     refreshed and the local state syncs via the prop key.
 *
 * Stop click propagation so editing doesn't trigger row selection.
 *
 * Goals are append-only. Every save is a new row server-side; we don't
 * track edit history in V1.
 *
 * Pinned-mode behavior (V1.1 plan §5.6): when `pinned` is non-null the
 * input renders as static read-only text (no input element) with a
 * tooltip explaining why. Renders an em-dash when there is no goal to
 * show.
 */
import { useEffect, useState } from "react";
import { useUpsertGoal } from "@/hooks/useUpsertGoal";
import { type PinnedContext, pinnedTooltip } from "../types";

export interface GoalCellProps {
  clinicId: number;
  year: number;
  measureId: number;
  currentGoal: string | null;
  pinned: PinnedContext | null;
}

export function GoalCell({
  clinicId,
  year,
  measureId,
  currentGoal,
  pinned,
}: GoalCellProps) {
  const upsert = useUpsertGoal(clinicId);
  const [value, setValue] = useState(currentGoal ?? "");

  // Sync local state when the server value changes (e.g. after a successful
  // save invalidates the dashboard query and a fresh `currentGoal` arrives).
  useEffect(() => {
    setValue(currentGoal ?? "");
  }, [currentGoal]);

  // Pinned mode: render as static read-only text. Per V1.1 plan §5.6 we
  // intentionally do NOT render an input — even a disabled one — to make
  // it crystal-clear nothing can be typed here.
  if (pinned) {
    const display = (currentGoal ?? "").trim() === "" ? "—" : currentGoal;
    return (
      <span
        title={pinnedTooltip(pinned)}
        className="block w-full px-1.5 py-0.5 text-sm text-gray-600"
      >
        {display}
      </span>
    );
  }

  const onBlur = () => {
    const trimmed = value.trim();
    const original = (currentGoal ?? "").trim();
    if (trimmed === original) return;
    if (trimmed === "") return; // empty input → no save (treat as no goal)
    upsert.mutate({
      measureId,
      year,
      body: { goal_text: trimmed },
    });
  };

  return (
    <input
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={onBlur}
      onClick={(e) => e.stopPropagation()}
      disabled={upsert.isPending}
      placeholder="Add goal…"
      aria-label="Measure goal"
      className="w-full rounded border border-transparent bg-transparent px-1.5 py-0.5 text-sm hover:border-border focus:border-tw-teal-500 focus:bg-white focus:outline-none disabled:opacity-50"
    />
  );
}
